/**
 * AdmissionKitWizard — bloqueia o EmployeeDashboard sempre que o
 * funcionário tiver um Kit de Admissão pendente. O wizard tem 8 passos
 * (1 por página do kit). Em cada passo o funcionário VÊ o PDF da
 * página, marca "Li e concordo" e avança. A página 2 inclui a opção
 * SIM/NÃO do Vale-Transporte com sub-checkboxes. Após as 8 páginas,
 * o app pede o OTP via SMS para confirmar.
 *
 * Não é possível fechar o modal antes de assinar — só após `done` (ou
 * caso o admin tenha removido o kit, o que disparará reload).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import SignatureEditDialog from "./SignatureEditDialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  ShieldCheck, FileText, ChevronLeft, ChevronRight, Loader2,
  CheckCircle2, Phone, Mail, KeyRound, AlertTriangle, BadgeCheck,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

const TOTAL_PAGES = 8;

const PAGE_TITLES = [
  "Ficha de Registro de Empregados",
  "Declaração de Vale-Transporte",
  "Contrato de Trabalho por Tempo Determinado",
  "Contrato LGPD (Proteção de Dados)",
  "Ordem de Serviço SST",
  "Formulário de Integração",
  "Termo de EPI",
  "Declaração de Recebimento de Crachá",
];

const VT_SIM_SUBS = [
  { key: "MUNICIPAL", label: "Municipal" },
  { key: "INTERMUNICIPAL", label: "Intermunicipal" },
  { key: "FORNECIDO PELA EMPRESA", label: "Fornecido pela empresa" },
];
const VT_NAO_SUBS = [
  { key: "TRANSPORTE PROPRIO", label: "Transporte próprio" },
  { key: "RESIDENCIA PROXIMA", label: "Residência próxima ao trabalho" },
  { key: "OUTROS", label: "Outros" },
];

const AdmissionKitWizard = ({ open, onSigned }) => {
  const { token, user } = useAuth();
  const [kit, setKit] = useState(null);
  const [rubricaOpen, setRubricaOpen] = useState(false);
  const [registeredPhone, setRegisteredPhone] = useState("");
  const [step, setStep] = useState("intro"); // intro|page-N|otp-phone|otp|done
  const [pageIdx, setPageIdx] = useState(1);
  const [pageUrl, setPageUrl] = useState("");
  const [pageConsents, setPageConsents] = useState(Array(TOTAL_PAGES).fill(false));
  const [vtChoice, setVtChoice] = useState(""); // SIM | NAO
  const [vtSubs, setVtSubs] = useState([]);
  const [phoneE164, setPhoneE164] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [registeredWhatsapp, setRegisteredWhatsapp] = useState("");
  const [whatsappStatus, setWhatsappStatus] = useState("pending");
  const [channel, setChannel] = useState("sms"); // sms | whatsapp | email
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [signatureId, setSignatureId] = useState("");

  // Load list of pending kits and pick the first
  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [r, prof] = await Promise.all([
        axios.get(`${API}/employee/admission-kits`, {
          params: { token }, timeout: 15000,
        }),
        axios.get(`${API}/employee/profile`, {
          params: { token }, timeout: 15000,
        }),
      ]);
      const pending = (r.data || []).filter((k) => k.status !== "signed");
      setKit(pending[0] || null);
      setRegisteredPhone(prof.data?.telefone || "");
      setRegisteredEmail(prof.data?.email || "");
      setRegisteredWhatsapp(prof.data?.whatsapp || "");
      setWhatsappStatus(
        (prof.data?.whatsapp_status || "pending").toLowerCase(),
      );
    } catch (e) {
      // silent — dashboard remains accessible if listing fails
    }
  }, [token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Fetch the current page PDF as blob URL when step changes
  useEffect(() => {
    if (!open || !kit || !step.startsWith("page-")) {
      if (pageUrl) URL.revokeObjectURL(pageUrl);
      setPageUrl("");
      return;
    }
    let revoked = false;
    let url = "";
    axios.get(
      `${API}/employee/admission-kits/${kit.recipient_id || kit._recipient_id}/page/${pageIdx}`,
      { params: { token }, responseType: "blob", timeout: 30000 },
    ).then((res) => {
      if (revoked) return;
      url = URL.createObjectURL(res.data);
      setPageUrl(url);
    }).catch(() => setPageUrl(""));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pageIdx, kit, token, open]);

  if (!open || !kit) return null;
  const recId = kit.recipient_id || kit._recipient_id;

  const allConsented = pageConsents.every(Boolean);
  const vtValid = vtChoice && (
    (vtChoice === "SIM" && vtSubs.length >= 1) ||
    (vtChoice === "NAO" && vtSubs.length >= 1)
  );

  const goPage = (n) => {
    if (n < 1 || n > TOTAL_PAGES) return;
    setPageIdx(n);
    setStep(`page-${n}`);
  };
  const startWizard = () => goPage(1);
  const nextFromPage = () => {
    if (pageIdx < TOTAL_PAGES) goPage(pageIdx + 1);
    else setStep("otp-phone");
  };

  const sendOtp = async () => {
    if (!vtValid) {
      toast.error("Responda o Vale-Transporte na página 2 antes de prosseguir.");
      goPage(2);
      return;
    }
    if (!allConsented) {
      toast.error("Você precisa marcar 'Li e concordo' em todas as 8 páginas.");
      const i = pageConsents.findIndex((c) => !c);
      goPage(Math.max(1, i + 1));
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/employee/admission-kits/${recId}/send-otp`,
        { token, channel }, { timeout: 30000 },
      );
      setPhoneE164(r.data?.phone_e164 || "");
      setEmailTo(r.data?.email || "");
      setStep("otp");
      const lbl = channel === "email" ? "e-mail" : "SMS";
      toast.success(`Código enviado por ${lbl}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar código");
    } finally { setBusy(false); }
  };

  const confirmOtp = async () => {
    const c = (code || "").trim();
    if (c.length < 4) { toast.error("Informe o código recebido"); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/employee/admission-kits/${recId}/confirm-otp`,
        {
          token, code: c, channel,
          transport_choice: vtChoice,
          transport_sub_choices: vtSubs,
          page_consents: pageConsents,
        },
        { timeout: 60000 },
      );
      setSignatureId(r.data?.signature_id || "");
      setStep("done");
      toast.success("Kit assinado com sucesso!");
      onSigned?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("rúbrica")) {
        setRubricaOpen(true);
      }
      toast.error(detail || "Código inválido");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not closable */ }}>
      <DialogContent
        className="max-w-2xl max-h-[94vh] flex flex-col p-4 sm:p-6 gap-3"
        data-testid="kit-wizard-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-emerald-600" />
            Kit de Admissão — Assinatura Obrigatória
          </DialogTitle>
          <DialogDescription>
            Você possui um Kit de Admissão pendente. Leia cada uma das 8
            páginas, marque "Li e concordo" e finalize com a confirmação
            por SMS.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-4 px-4 sm:-mx-6 sm:px-6 space-y-3">
          <AnimatePresence mode="wait">
            {step === "intro" && (
              <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm">
                  <p className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4" /> Antes de começar
                  </p>
                  <ul className="list-disc pl-5 text-amber-900 space-y-0.5">
                    <li>São 8 documentos curtos. Cada um pede sua leitura + concordância.</li>
                    <li>A página 2 (Vale-Transporte) tem uma escolha SIM/NÃO obrigatória.</li>
                    <li>Ao final, você receberá um SMS para confirmar a assinatura.</li>
                    <li>Você não conseguirá usar o app até concluir esta assinatura.</li>
                  </ul>
                </div>
                <Button
                  className="w-full"
                  onClick={startWizard}
                  data-testid="kit-start-btn"
                >
                  Iniciar — Página 1 de {TOTAL_PAGES}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            )}

            {step.startsWith("page-") && (
              <motion.div key={step} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                    <FileText className="w-4 h-4 text-sky-600" />
                    Página {pageIdx} de {TOTAL_PAGES} — {PAGE_TITLES[pageIdx - 1]}
                  </div>
                  <div className="flex gap-1">
                    {Array.from({ length: TOTAL_PAGES }).map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 w-4 rounded-full ${
                          pageConsents[i] ? "bg-emerald-500"
                            : i + 1 === pageIdx ? "bg-sky-500" : "bg-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {pageUrl ? (
                  <div className="rounded-md overflow-hidden border border-slate-300 bg-slate-50">
                    <iframe
                      src={pageUrl}
                      title={`Kit página ${pageIdx}`}
                      className="w-full h-[300px] sm:h-[480px]"
                      data-testid={`kit-page-iframe-${pageIdx}`}
                    />
                  </div>
                ) : (
                  <div className="rounded-md border border-slate-300 bg-slate-50 h-[300px] sm:h-[480px] flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  </div>
                )}

                {pageIdx === 2 && (
                  <div className="rounded-md bg-sky-50 border border-sky-200 p-3 space-y-3">
                    <p className="text-xs font-semibold text-sky-900 uppercase">
                      Vale-Transporte — escolha obrigatória
                    </p>
                    <div className="flex gap-3" data-testid="kit-vt-choice">
                      {[
                        { key: "SIM", label: "SIM, quero o benefício" },
                        { key: "NAO", label: "NÃO, recuso o benefício" },
                      ].map((o) => (
                        <button
                          key={o.key}
                          type="button"
                          className={`flex-1 px-3 py-2 rounded border text-sm font-medium transition ${
                            vtChoice === o.key
                              ? "bg-sky-600 text-white border-sky-700"
                              : "bg-white text-slate-700 border-slate-300 hover:border-sky-400"
                          }`}
                          onClick={() => { setVtChoice(o.key); setVtSubs([]); }}
                          data-testid={`kit-vt-${o.key.toLowerCase()}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {vtChoice && (
                      <div className="space-y-1">
                        <p className="text-xs text-slate-600">
                          {vtChoice === "SIM"
                            ? "Tipo de condução utilizada (marque 1+):"
                            : "Justifique a recusa (marque 1+):"}
                        </p>
                        {(vtChoice === "SIM" ? VT_SIM_SUBS : VT_NAO_SUBS).map((s) => (
                          <label
                            key={s.key}
                            className="flex items-center gap-2 text-sm cursor-pointer"
                          >
                            <Checkbox
                              checked={vtSubs.includes(s.key)}
                              onCheckedChange={(v) => {
                                setVtSubs((prev) => v
                                  ? [...prev, s.key]
                                  : prev.filter((x) => x !== s.key));
                              }}
                              data-testid={`kit-vt-sub-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
                            />
                            {s.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={pageConsents[pageIdx - 1]}
                      onCheckedChange={(v) => {
                        setPageConsents((prev) => {
                          const next = [...prev];
                          next[pageIdx - 1] = !!v;
                          return next;
                        });
                      }}
                      className="mt-0.5"
                      data-testid={`kit-page-consent-${pageIdx}`}
                    />
                    <span className="text-xs text-amber-900 leading-relaxed select-none">
                      <strong>DECLARO QUE LI E CONCORDO COM O CONTEÚDO
                        DESTE DOCUMENTO</strong>, reconhecendo a validade
                      da minha assinatura digital aplicada nesta página.
                    </span>
                  </label>
                </div>
              </motion.div>
            )}

            {step === "otp-phone" && (
              <motion.div key="otp-phone" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-900">
                  ✅ Você revisou e concordou com as 8 páginas. Vamos
                  confirmar sua identidade.
                </div>
                {/* Channel selector */}
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 space-y-2">
                  <p className="text-xs font-medium text-slate-700">
                    Como deseja receber o código?
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setChannel("sms")}
                      disabled={!registeredPhone}
                      className={`text-left rounded-md border p-2 transition ${
                        channel === "sms"
                          ? "border-emerald-600 bg-white shadow-sm"
                          : "border-slate-200 bg-white/50 hover:bg-white"
                      } ${!registeredPhone ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid="kit-channel-sms"
                    >
                      <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> SMS
                      </div>
                      <div className="text-[10px] text-slate-600 truncate">
                        {registeredPhone || "Sem telefone"}
                      </div>
                    </button>
                    {/* WhatsApp OTP desabilitado em 2026-05-28 — Meta restringe
                        Z-API no 1º envio transacional. Reabilitar quando a
                        WhatsApp Business API oficial (360dialog) estiver ativa. */}
                    <button type="button" onClick={() => setChannel("email")}
                      disabled={!registeredEmail}
                      className={`text-left rounded-md border p-2 transition ${
                        channel === "email"
                          ? "border-blue-600 bg-white shadow-sm"
                          : "border-slate-200 bg-white/50 hover:bg-white"
                      } ${!registeredEmail ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid="kit-channel-email"
                    >
                      <div className="text-xs font-semibold text-blue-700 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> E-mail
                      </div>
                      <div className="text-[10px] text-slate-600 truncate">
                        {registeredEmail || "Sem e-mail"}
                      </div>
                    </button>
                  </div>
                </div>

                <Label>{channel === "email" ? "E-mail cadastrado" : "Telefone cadastrado"}</Label>
                <div className="relative">
                  {channel === "email" ? (
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  ) : (
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  )}
                  <Input
                    className="pl-9 bg-slate-50 cursor-not-allowed font-mono"
                    value={
                      channel === "email"
                        ? (registeredEmail || "(carregando...)")
                        : (registeredPhone || "(carregando...)")
                    }
                    readOnly disabled
                    data-testid="kit-otp-phone-locked"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Para alterar este {channel === "email" ? "e-mail" : "número"}, fale com o administrador.
                </p>
              </motion.div>
            )}

            {step === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                <Label htmlFor="kit-otp">Código recebido por {channel === "email" ? "e-mail" : "SMS"}</Label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="kit-otp"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="000000"
                    className="pl-9 tracking-widest font-mono text-lg"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="kit-otp-input"
                    autoFocus
                  />
                </div>
                {phoneE164 && (
                  <p className="text-xs text-slate-500">
                    Código enviado para <span className="font-mono">{phoneE164}</span>.
                  </p>
                )}
              </motion.div>
            )}

            {step === "done" && (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-slate-900">
                  Kit de Admissão assinado com sucesso!
                </p>
                {signatureId && (
                  <div className="inline-block mt-3 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 text-xs font-mono text-emerald-800">
                    ID: <span className="font-semibold">{signatureId}</span>
                  </div>
                )}
                <p className="text-sm text-slate-500 mt-3">
                  Uma cópia foi enviada para o RH e para o seu e-mail
                  cadastrado.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <DialogFooter className="shrink-0 flex-col-reverse sm:flex-row gap-2">
          {step.startsWith("page-") && (
            <>
              {pageIdx > 1 && (
                <Button
                  variant="outline" onClick={() => goPage(pageIdx - 1)}
                  disabled={busy}
                  data-testid="kit-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
              )}
              <Button
                onClick={nextFromPage}
                disabled={
                  busy ||
                  !pageConsents[pageIdx - 1] ||
                  (pageIdx === 2 && !vtValid)
                }
                data-testid="kit-next-btn"
              >
                {pageIdx < TOTAL_PAGES ? "Próximo" : "Revisar e enviar SMS"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === "otp-phone" && (
            <Button
              onClick={() =>
                user?.has_signature ? sendOtp() : setRubricaOpen(true)
              }
              disabled={busy}
              data-testid="kit-send-otp-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Enviando…</>
                : `Enviar código por ${channel === "email" ? "e-mail" : channel === "whatsapp" ? "WhatsApp" : "SMS"}`}
            </Button>
          )}
          {step === "otp" && (
            <Button onClick={confirmOtp} disabled={busy} data-testid="kit-confirm-otp-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Validando…</>
                : "Confirmar e Assinar"}
            </Button>
          )}
          {step === "done" && (
            <Button
              onClick={() => { setKit(null); onSigned?.(); }}
              data-testid="kit-done-btn"
            >
              Concluir
            </Button>
          )}
        </DialogFooter>

        <SignatureEditDialog
          open={rubricaOpen}
          onOpenChange={setRubricaOpen}
          hasExisting={!!user?.has_signature}
          onSaved={() => {
            if (step === "otp-phone") sendOtp();
            else toast.info("Rúbrica salva — toque em Confirmar e Assinar.");
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AdmissionKitWizard;
